
  import { Vue, Component, Prop } from "vue-property-decorator";
  import storage from "@/services/storage";

  import DELETE_DOCUMENT_CONTRACTOR from "@/apollo/documents/DeleteDocumentContractor.gql";
  import DELETE_DOCUMENT_MANAGEMENT from "@/apollo/documents/DeleteDocumentManagement.gql";

  import splitFileNameExt from "@/utils/splitFileNameExt";

  import { NOTIFICATION } from "@/entities/Events";
  import Message from "@/data/notificationsMessages";
  import { Document } from "@/generated/graphql";
  import { copyToClipboard } from "@/helpers/clipboardActions";
  import { defaultErrorNotificationMessage } from "@/data/errorMessages";
  import Roles from "@/entities/Roles";

  @Component
  export default class DocumentProvider extends Vue {
    @Prop({ type: Object, required: true }) readonly document!: Document;
    @Prop({ type: String, required: true }) readonly currentUserRole!: string;

    processing = false;

    async copyLink(): Promise<void> {
        const splitName = splitFileNameExt(this.document.filename!);
        const filename = `${splitName.name}-${this.document.id}.${splitName.ext}`;
        const filepath = await this.getFile(filename);
        const copied = copyToClipboard(filepath);

        if (copied) {
          return this.$eventBus.emit(NOTIFICATION, Message.successMessage("Link copied to clipboard"));
        }

        this.$eventBus.emit(NOTIFICATION, Message.errorMessage(defaultErrorNotificationMessage));
    }

    async download(): Promise<void> {
      try {
        const splitName = splitFileNameExt(this.document.filename!);
        const filename = `${splitName.name}-${this.document.id}.${splitName.ext}`;
        const file = await this.getFile(filename);
        window.open(file, "_self");
        this.$eventBus.emit(NOTIFICATION, Message.successMessage(`${this.document.filename} - downloaded successfully.`));
      } catch (e) {
        this.$eventBus.emit(NOTIFICATION, Message.errorMessage(e.message));
      }
    }

    async getFile(filename: string) {
      return await storage.get(filename)
        .then((result: any) => result)
        .catch((err: any) => {
          throw err;
        });
    }

    async remove(): Promise<void> {
      this.processing = true;

      try {
        const splitName = splitFileNameExt(this.document.filename!);
        const s3Filename = `${splitName.name}-${this.document.id}.${splitName.ext}`;
        await this.deleteDocumentInformation(this.document.id);
        await this.deleteFile(s3Filename);
        this.$emit("documentDeleted", this.document.id);

        this.$eventBus.emit(NOTIFICATION, Message.successMessage("File was deleted successfully."));
      } catch (e) {
        this.$eventBus.emit(NOTIFICATION, Message.errorMessage("Something went wrong!"));
      } finally {
        this.processing = false;
      }
    }

    async deleteDocumentInformation(id: string) {
      const { data } = await this.$apollo.mutate({
        mutation: this.currentUserRole === Roles.CONTRACTOR ? DELETE_DOCUMENT_CONTRACTOR : DELETE_DOCUMENT_MANAGEMENT,
        variables: { id },
      });

      if (!data.delete_document_by_pk) {
        throw new Error();
      }
    }

    async deleteFile(key: string) {
      await storage.delete(key)
        .then((result: any) => result)
        .catch((err: any) => {
          throw err;
        });
    }
  }
